import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {TablerPlus} from '../../icons';
import ModalFrame from '../common/ModalFrame';
import {useUser} from '../../contexts/UserContext';
import usePlannerStyles from './usePlannerStyles';
import {
  CallerId,
  listCallerIds,
  verifyCallerId,
} from '../../services/callerIdService';

// Loose E.164 normalizer: keep a leading +, otherwise assume US when 10 digits.
const toE164 = (raw: string): string => {
  const trimmed = (raw || '').trim();
  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1).replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  }
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return digits ? `+${digits}` : '';
};

const formatDisplay = (e164: string): string => {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  if (m) return `(${m[1]}) ${m[2]}-${m[3]}`;
  return e164;
};

const optionLabel = (caller: CallerId): string => {
  const number = formatDisplay(caller.phoneNumber);
  if (caller.status === 'pending') return `${caller.label} — verifying…`;
  if (caller.status === 'failed')
    return `${caller.label} — verification failed, re-add to retry`;
  return `${caller.label} — ${number}`;
};

const POLL_MS = 3000;

export default function CallerIdField({
  value,
  setCallerId,
  dialTarget = 'patient',
}: {
  value: string;
  setCallerId: (next: string) => void;
  /**
   * Who the outbound call rings — drives the helper copy and the default.
   * Calls that ring the provider ('provider'/'both') default to the org's
   * first verified number: an internal caller ID rings straight through the
   * clinic's phone system instead of landing in the IVR queue. Patient-only
   * dials keep the default Opalite number.
   */
  dialTarget?: 'patient' | 'provider' | 'both';
}) {
  const styles = usePlannerStyles();
  const {borderColor, inputFocusStyles, subtleText, textColor, trackBg} =
    styles;
  const {userData} = useUser();
  const hasCompany = Boolean(userData?.companyId);
  const toast = useToast();
  const addModal = useDisclosure();

  const [callerIds, setCallerIds] = useState<CallerId[]>([]);
  const [loading, setLoading] = useState(false);
  // True once the provider picks an option by hand — defaulting keeps its
  // hands off after that. autoSelectedRef marks a default THIS component
  // applied, so flipping back to a patient-only dial can undo its own choice
  // (and only its own choice).
  const userPickedRef = useRef(false);
  const autoSelectedRef = useRef(false);

  // Add/verify modal state
  const [label, setLabel] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validationCode, setValidationCode] = useState('');
  const [pendingNumber, setPendingNumber] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    if (!hasCompany) return [];
    setLoading(true);
    try {
      const list = await listCallerIds();
      setCallerIds(list);
      return list;
    } catch (error) {
      // Non-fatal: the picker just shows the default option.
      return [];
    } finally {
      setLoading(false);
    }
  }, [hasCompany]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Provider-facing dials default to the org's first verified number (the
  // "internal" caller ID); patient-only dials keep the Opalite default. Runs
  // only until the provider picks something themselves.
  useEffect(() => {
    if (userPickedRef.current) return;
    const firstVerified = callerIds.find(c => c.status === 'verified');
    if (dialTarget !== 'patient') {
      if (!value && firstVerified) {
        autoSelectedRef.current = true;
        setCallerId(firstVerified.phoneNumber);
      }
    } else if (autoSelectedRef.current) {
      autoSelectedRef.current = false;
      setCallerId('');
    }
  }, [dialTarget, callerIds, value, setCallerId]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const closeModal = useCallback(() => {
    stopPolling();
    setSubmitting(false);
    setValidationCode('');
    setPendingNumber('');
    setLabel('');
    setPhone('');
    addModal.onClose();
  }, [addModal, stopPolling]);

  const handleVerify = useCallback(async () => {
    const e164 = toE164(phone);
    const trimmedLabel = label.trim();
    if (!/^\+[1-9]\d{6,14}$/.test(e164)) {
      toast({
        title: 'Enter a valid phone number',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    if (!trimmedLabel) {
      toast({title: 'Add a label', status: 'warning', duration: 3000});
      return;
    }

    setSubmitting(true);
    try {
      const result = await verifyCallerId({
        phoneNumber: e164,
        label: trimmedLabel,
      });

      if (result.alreadyVerified) {
        await refresh();
        userPickedRef.current = true;
        setCallerId(e164);
        toast({
          title: 'Caller ID verified',
          status: 'success',
          duration: 3000,
        });
        closeModal();
        return;
      }

      // Verification call placed — show the code and poll until it lands.
      setValidationCode(result.validationCode || '');
      setPendingNumber(e164);
      await refresh();

      stopPolling();
      pollRef.current = setInterval(async () => {
        const list = await refresh();
        const match = list.find(c => c.phoneNumber === e164);
        if (match?.status === 'verified') {
          stopPolling();
          userPickedRef.current = true;
          setCallerId(e164);
          toast({
            title: 'Caller ID verified',
            status: 'success',
            duration: 3000,
          });
          closeModal();
        } else if (match?.status === 'failed') {
          // Call unanswered / wrong code — back to the form (label and number
          // stay filled) so retrying is one click.
          stopPolling();
          setValidationCode('');
          setPendingNumber('');
          toast({
            title: 'Verification failed',
            description:
              "The call wasn't completed with the right code. Try again.",
            status: 'error',
            duration: 6000,
          });
        }
      }, POLL_MS);
    } catch (error: any) {
      toast({
        title: 'Verification failed',
        description:
          error?.payload?.message || error?.message || 'Please try again.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  }, [phone, label, refresh, setCallerId, toast, closeModal, stopPolling]);

  const verifiedIds = callerIds.filter(c => c.status === 'verified');
  const pendingIds = callerIds.filter(c => c.status === 'pending');
  const failedIds = callerIds.filter(c => c.status === 'failed');

  const audienceCopy =
    dialTarget === 'patient'
      ? 'The number your patient sees when Opalite calls them.'
      : dialTarget === 'provider'
        ? "The number shown when Opalite calls you — your organization's verified number rings straight through most clinic phone systems instead of the main queue."
        : "The number you and your patient see when Opalite calls — your organization's verified number rings straight through most clinic phone systems.";

  return (
    <FormControl mt={4}>
      <FormLabel color={textColor} fontWeight="normal" fontSize="sm">
        Caller ID
      </FormLabel>
      <HStack spacing={2} align="stretch">
        <Select
          flex="1"
          value={value}
          onChange={event => {
            userPickedRef.current = true;
            setCallerId(event.target.value);
          }}
          borderRadius="lg"
          borderColor={borderColor}
          _hover={{borderColor}}
          _focusVisible={inputFocusStyles}
          aria-label="Outbound caller ID"
          isDisabled={!hasCompany}
        >
          <option value="">Default (Opalite number)</option>
          {verifiedIds.map(caller => (
            <option key={caller.id} value={caller.phoneNumber}>
              {optionLabel(caller)}
            </option>
          ))}
          {pendingIds.map(caller => (
            <option key={caller.id} value="" disabled>
              {optionLabel(caller)}
            </option>
          ))}
          {failedIds.map(caller => (
            <option key={caller.id} value="" disabled>
              {optionLabel(caller)}
            </option>
          ))}
        </Select>
        <Button
          type="button"
          variant="outline"
          size="md"
          leftIcon={<TablerPlus size={16} aria-hidden="true" />}
          onClick={addModal.onOpen}
          isDisabled={!hasCompany}
          whiteSpace="nowrap"
        >
          Add
        </Button>
      </HStack>
      <Text fontSize="xs" color={subtleText} mt={2}>
        {audienceCopy} Verified numbers are shared with everyone at{' '}
        {userData?.companyName || 'your organization'}.
      </Text>

      <ModalFrame
        isOpen={addModal.isOpen}
        onClose={closeModal}
        title={
          <Text as="span" fontWeight="700">
            Add a caller ID
          </Text>
        }
        isCentered
        size="md"
        contentProps={{borderRadius: 'xl', mx: 4}}
        bodyProps={{pb: 6}}
      >
        {validationCode ? (
          <VStack align="stretch" spacing={4}>
            <Box
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="lg"
              bg={trackBg}
              px={4}
              py={4}
            >
              <Text fontSize="sm" color={textColor}>
                We&rsquo;re calling{' '}
                <Text as="span" fontWeight="700">
                  {formatDisplay(pendingNumber)}
                </Text>
                . When prompted, enter this code:
              </Text>
              <Text
                fontSize="3xl"
                fontWeight="700"
                letterSpacing="0.2em"
                textAlign="center"
                color={textColor}
                mt={2}
              >
                {validationCode}
              </Text>
            </Box>
            <HStack spacing={2} color={subtleText} fontSize="sm">
              <Spinner size="sm" />
              <Text>Waiting for verification…</Text>
            </HStack>
            <Text fontSize="xs" color={subtleText}>
              You can close this — the number will appear once verified.
            </Text>
            <Button variant="outline" onClick={closeModal}>
              Close
            </Button>
          </VStack>
        ) : (
          <VStack align="stretch" spacing={4}>
            <FormControl>
              <FormLabel color={textColor} fontWeight="normal" fontSize="sm">
                Label
              </FormLabel>
              <Input
                value={label}
                onChange={event => setLabel(event.target.value)}
                placeholder="CIFC BH Reception"
                borderRadius="lg"
                borderColor={borderColor}
                _hover={{borderColor}}
                _focusVisible={inputFocusStyles}
              />
            </FormControl>
            <FormControl>
              <FormLabel color={textColor} fontWeight="normal" fontSize="sm">
                Phone number
              </FormLabel>
              <Input
                value={phone}
                onChange={event => setPhone(event.target.value)}
                placeholder="(555) 123-4567"
                type="tel"
                borderRadius="lg"
                borderColor={borderColor}
                _hover={{borderColor}}
                _focusVisible={inputFocusStyles}
              />
            </FormControl>
            <Text fontSize="xs" color={subtleText}>
              Opalite will place a short verification call to this number.
              Someone must answer and enter the code we show you.
            </Text>
            <Button
              variant="opalitePrimary"
              onClick={handleVerify}
              isLoading={submitting}
              loadingText="Calling…"
            >
              Call to verify
            </Button>
          </VStack>
        )}
      </ModalFrame>
    </FormControl>
  );
}
